import { WorkspaceAssistant } from '@/features/assistant/WorkspaceAssistant';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { useMembers } from '@/features/members';
import { StudySetup } from '@/features/imaging';
import { useOrgDateFormat } from '@/lib/timezone';
import { useCalendar } from '../api/calendar-api';
import { MonthNav } from '../components/MonthNav';
import { Skeleton } from '@/components/ui/skeleton';
import { MonthGrid } from '../components/MonthGrid';
import { AgendaList } from '../components/AgendaList';
import { DaySchedule } from '../components/DaySchedule';
import type { CalendarWindow } from '../api/calendar-api';
import { monthOf, monthRange } from '@/lib/calendar/monthGrid';
import { P, PermissionGuard, usePermissions } from '@/features/permissions';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import { useImagingCapabilities, useImagingQuery } from '@/features/imaging';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const ALL_CLINICIANS = 'all';

export function CalendarPage() {
  return (
    <PermissionGuard permission={P.READ_ENCOUNTER}>
      <CalendarPageContent />
    </PermissionGuard>
  );
}

/**
 * The month grid.
 *
 * Reads encounters — there is no appointments table, and a visit already has a
 * date, an optional appointment instant and an assigned clinician.
 *
 * Which cell an encounter lands in is decided by `encounter_date` alone;
 * `scheduled_at` only supplies the time label. That asymmetry is deliberate:
 * `encounter_date` carries no timezone, so nothing can shift it, whereas a grid
 * keyed on the instant would move historical visits to different days the moment
 * an administrator changed the practice's zone.
 */
function CalendarPageContent() {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const navigate = useNavigate();
  const setPageHeader = usePageHeaderSetter();
  const { today } = useOrgDateFormat();
  const { can } = usePermissions();

  const search = useSearch({ from: '/_authenticated/calendar' });

  // The practice's today, not the browser's. For a UTC+11 clinic a device in
  // another zone is on the previous month for part of every 1st, so a local
  // default would open the calendar on the wrong month.
  const month = search.month ?? monthOf(today());
  const assignedTo = search.assigned_to;

  const { to, from } = monthRange(month);
  const visibleSelectedDate = selectedDate && selectedDate >= from && selectedDate <= to ? selectedDate : null;
  const { data, isError, isLoading } = useCalendar({
    date_to: to,
    date_from: from,
    ...(assignedTo ? { assigned_to: assignedTo } : {}),
  });

  // The month lives in the URL so a link to it is shareable and the back button
  // steps months. Both keys are always written: TanStack's search type wants the
  // whole shape, and an explicit `undefined` is how "All clinicians" clears the
  // filter rather than leaving a stale one behind in the URL.
  const go = useCallback(
    (next: { assigned_to?: string; month?: string }) =>
      navigate({ to: '/calendar', search: { month, assigned_to: assignedTo, ...next } }),
    [navigate, month, assignedTo],
  );

  useEffect(() => {
    setPageHeader({
      title: 'Calendar',
      description: "Encounters and imaging studies, in the practice's timezone",
      actions: (
        <div className="flex items-center gap-2">
          <WorkspaceAssistant
            title="Calendar assistant"
            commands={[
              {
                label: 'Show this month',
                run: () => {
                  void go({ month: monthOf(today()) });
                  setSelectedDate(today());
                },
              },
              {
                label: 'Show all clinicians',
                run: () => {
                  void go({ assigned_to: undefined });
                },
              },
            ]}
          />
          <MonthNav month={month} onChange={(next) => go({ month: next })} />
        </div>
      ),
    });
    return () => setPageHeader(null);
  }, [setPageHeader, month, go]);

  const imagingAccess = useImagingCapabilities();
  const canReadImaging = imagingAccess.data?.permissions.some((p) => ['prepare_imaging', 'read_imaging'].includes(p));
  const imaging = useImagingQuery<CalendarWindow>(`/calendar?date_from=${from}&date_to=${to}`, !!canReadImaging);
  const cutoffs = [data, canReadImaging && !assignedTo ? imaging.data : undefined]
    .filter((v) => v?.truncated)
    .map((v) => v!.complete_through || from);
  const completeThrough = cutoffs.length ? cutoffs.sort()[0] : null;
  const entries = [...(data?.entries || []), ...(!assignedTo && canReadImaging ? imaging.data?.entries || [] : [])]
    .filter((e) => !completeThrough || e.encounter_date <= completeThrough)
    .sort(
      (a, b) =>
        a.encounter_date.localeCompare(b.encounter_date) ||
        (a.scheduled_local_time || '').localeCompare(b.scheduled_local_time || ''),
    );

  return (
    <div className="mt-4 flex flex-col gap-6 px-4">
      {imagingAccess.data?.permissions.includes('intake_imaging') && <StudySetup />}
      {imagingAccess.isError && (
        <p role="alert">Imaging access could not be checked. Imaging appointments may be missing.</p>
      )}
      {imaging.isError && canReadImaging && (
        <p role="alert">Imaging appointments could not load. The calendar may be incomplete.</p>
      )}
      {imaging.data?.truncated && (
        <p role="alert">Imaging appointments are incomplete after {imaging.data.complete_through}.</p>
      )}
      {assignedTo && <p>Imaging studies are unassigned appointments. Select All clinicians to include them.</p>}
      {can(P.MANAGE_USERS) && <ClinicianFilter value={assignedTo} onChange={(id) => go({ assigned_to: id })} />}

      {/*
       * Stated rather than left to be inferred. The grid greys the cells it
       * could not fill, but a clinician looking at one day has no way to tell a
       * quiet Thursday from a truncated one without being told the window was
       * cut.
       */}
      {data?.truncated && (
        <p className="rounded-[12px] border border-warning-border bg-warning-surface px-4 py-3 text-sm text-warning">
          This month has more encounters than the calendar loads at once. Days after {data.complete_through} are not
          shown — filter by clinician to see them.
        </p>
      )}

      {isError && (
        <p className="rounded-[12px] border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Could not load the calendar. Try again, or step to another month.
        </p>
      )}

      {isLoading || imagingAccess.isLoading || (canReadImaging && imaging.isLoading) ? (
        <Skeleton className="h-[560px] w-full rounded-[18px]" />
      ) : (
        <>
          <div
            className={
              visibleSelectedDate
                ? 'hidden md:grid md:gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(350px,2fr)]'
                : 'hidden md:block'
            }>
            <div className="min-w-0 overflow-x-auto">
              <div className={visibleSelectedDate ? 'min-w-[700px]' : undefined}>
                <MonthGrid
                  month={month}
                  today={today()}
                  entries={entries}
                  completeThrough={completeThrough}
                  selectedDate={visibleSelectedDate}
                  onSelectDate={setSelectedDate}
                />
              </div>
            </div>
            {visibleSelectedDate && (
              <DaySchedule
                assignedTo={assignedTo}
                date={visibleSelectedDate}
                canReadImaging={!!canReadImaging}
                onClose={() => setSelectedDate(null)}
              />
            )}
          </div>

          <div className="md:hidden">
            {visibleSelectedDate && (
              <div className="mb-4">
                <DaySchedule
                  assignedTo={assignedTo}
                  date={visibleSelectedDate}
                  canReadImaging={!!canReadImaging}
                  onClose={() => setSelectedDate(null)}
                />
              </div>
            )}
            <AgendaList entries={entries} onSelectDate={setSelectedDate} />
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Shown only to members who can list the organization's users, because that is
 * what `GET /members` requires. A clinician without it sees an unfiltered
 * calendar rather than a dropdown that 403s.
 */
function ClinicianFilter({ value, onChange }: { onChange: (id: string | undefined) => void; value?: string }) {
  const { data: members } = useMembers();

  const clinicians = (members ?? [])
    .filter((member) => member.is_active && member.user_name)
    .map((member) => ({ id: member.user_id, name: member.user_name as string }));

  return (
    <Select
      value={value ?? ALL_CLINICIANS}
      onValueChange={(next) => onChange(next === ALL_CLINICIANS ? undefined : next)}>
      <SelectTrigger className="w-[220px]">
        <SelectValue placeholder="All clinicians" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_CLINICIANS}>All clinicians</SelectItem>
        {clinicians.map((clinician) => (
          <SelectItem key={clinician.id} value={clinician.id}>
            {clinician.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
